import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, TrendingDown, Leaf, DollarSign, AlertTriangle, Lightbulb, BarChart3, Camera, Image as ImageIcon } from 'lucide-react';
import Layout from './Layout';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  suggestions?: string[];
  image?: string;
}

const AIAssistant: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hello! I'm your AI Climate Assistant. I can help you with emission trends, carbon credit trading, pollution reduction strategies, and environmental compliance. What would you like to know?",
      sender: 'ai',
      timestamp: new Date(),
      suggestions: [
        'Show global emission trends for steel industry',
        'How can I reduce emissions in cement production?',
        'Carbon credit trading opportunities',
        'Department-wise pollution analysis'
      ]
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeImage = async (imageData: string) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    
    try {
      const base64Data = imageData.split(',')[1];
      const response = await fetch(`${API_URL}/api/analyze-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          image: base64Data,
          context: input || 'emission monitoring'
        }),
      });

      if (!response.ok) {
        throw new Error('Image analysis failed');
      }

      const data = await response.json();
      return data.analysis;
    } catch (error) {
      console.error('Error analyzing image:', error);
      return 'Unable to analyze image. Please ensure the backend is running and try again.';
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getAIResponseFromBackend = async (userMessage: string): Promise<{ text: string; suggestions?: string[] }> => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    console.log('🔍 API_URL:', API_URL);
    console.log('🔍 Calling backend with message:', userMessage);
    
    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: userMessage }),
      });

      if (!response.ok) {
        console.error('❌ Backend error:', response.status, response.statusText);
        throw new Error('Backend API error');
      }

      const data = await response.json();
      console.log('✅ Backend response:', data);
      return {
        text: data.response,
        suggestions: [
          'Tell me more about this',
          'What are the next steps?',
          'Show me alternatives',
          'Calculate the ROI'
        ]
      };
    } catch (error) {
      console.error('❌ Error calling backend:', error);
      console.log('⚠️ Falling back to hardcoded responses');
      return getAIResponseFallback(userMessage);
    }
  };

  const getAIResponseFallback = (userMessage: string): { text: string; suggestions?: string[] } => {
    const msg = userMessage.toLowerCase();

    // Emission trends queries
    if (msg.includes('emission') && (msg.includes('trend') || msg.includes('global') || msg.includes('steel') || msg.includes('cement'))) {
      return {
        text: `📊 **Global Emission Trends (2024)**

**Steel Industry:**
- Global: 2.6 Gt CO₂/year (7% of total emissions)
- Top emitters: China (1.1 Gt), India (280 Mt), Japan (170 Mt)
- Trend: -2.3% YoY with green hydrogen adoption
- Blast furnace coal: 770 kg CO₂/ton steel

**Cement Industry:**
- Global: 2.9 Gt CO₂/year (8% of total emissions)
- Top emitters: China (1.5 Gt), India (340 Mt), Vietnam (95 Mt)
- Trend: -1.8% YoY with alternative fuels
- Clinker production: 900 kg CO₂/ton cement

**Key Insights:**
✓ Biogas integration reducing steel emissions by 35-45%
✓ Waste-derived fuels displacing 30% coal in cement
✓ CCS adoption growing at 18% annually`,
        suggestions: [
          'How to reduce steel plant emissions?',
          'Carbon credit opportunities',
          'Compare my plant performance'
        ]
      };
    }

    // Reduction strategies
    if (msg.includes('reduce') || msg.includes('prevent') || msg.includes('lower')) {
      return {
        text: `🌱 **Emission Reduction Strategies**

**For Steel Plants:**
1. **Biogas Integration** (35-45% reduction)
   - Replace coal with biogas in blast furnaces
   - ROI: 2.3 years | Cost: $1.2M per plant

2. **Electric Arc Furnace** (60% reduction)
   - Transition from blast furnace
   - Uses scrap steel, renewable electricity

3. **Hydrogen Direct Reduction** (95% reduction)
   - Green H₂ replaces coking coal
   - Pilot stage, commercial by 2027

**For Cement Plants:**
1. **Alternative Fuels** (30-40% reduction)
   - RDF, biomass, waste oils
   - Your current: 23% → Target: 45%

2. **Clinker Substitution** (20-25% reduction)
   - Fly ash, slag, calcined clay
   - Reduces limestone calcination

3. **Carbon Capture** (90% reduction)
   - Post-combustion capture
   - Cost: $60-80/ton CO₂

**Quick Wins:**
✓ Energy efficiency audits (5-10% savings)
✓ Waste heat recovery (8-12% savings)
✓ Process optimization with AI (3-7% savings)`,
        suggestions: [
          'Calculate ROI for biogas integration',
          'Department-wise pollution analysis',
          'Show carbon credit potential'
        ]
      };
    }

    // Carbon credit trading
    if (msg.includes('carbon credit') || msg.includes('trading') || msg.includes('article 6')) {
      return {
        text: `💰 **Carbon Credit Trading Insights**

**Your Current Position:**
- Verified reductions: 12,847 tCO₂e
- Potential credits: 12,847 ITMOs
- Market value: $2.4M (at $187/credit)

**Trading Opportunities:**

**Article 6.2 Partnerships:**
1. **Sweden Partnership** - 4,200 credits
   - Price: $195/credit | Value: $819K
   - Status: Gold Standard verified

2. **Germany Partnership** - 3,800 credits
   - Price: $185/credit | Value: $703K
   - Focus: Industrial decarbonization

3. **South Africa Partnership** - 2,900 credits
   - Price: $175/credit | Value: $507.5K
   - Just transition focus

**Market Trends:**
- Voluntary market: $180-220/tCO₂e
- Compliance market: $85-95/tCO₂e (EU ETS)
- Article 6 premium: +15-25%

**Eligibility Criteria:**
✓ MRV verification (Gold Standard/Verra)
✓ Additionality proof
✓ No double counting
✓ Sustainable development co-benefits

**Next Steps:**
1. Complete MRV documentation
2. Register with UNFCCC registry
3. Engage authorized brokers`,
        suggestions: [
          'How to verify carbon credits?',
          'Find trading partners',
          'Calculate my credit potential'
        ]
      };
    }

    // Department analysis
    if (msg.includes('department') || msg.includes('pollution analysis') || msg.includes('source')) {
      return {
        text: `🏭 **Department-Wise Pollution Analysis**

**Steel Plant Breakdown:**

1. **Blast Furnace** (45% of emissions)
   - CO₂: 1,850 kg/ton steel
   - Action: Biogas injection, oxygen enrichment
   - Potential: -40% with biogas

2. **Coke Ovens** (25% of emissions)
   - CO₂: 1,200 kg/ton coke
   - Action: Coal moisture control, heat recovery
   - Potential: -15% efficiency gains

3. **Sinter Plant** (18% of emissions)
   - CO₂: 180 kg/ton sinter
   - Action: Waste heat recovery, fuel switching
   - Potential: -25% with optimization

4. **Power Generation** (12% of emissions)
   - Action: Renewable energy procurement
   - Potential: -60% with solar/wind

**Cement Plant Breakdown:**

1. **Kiln Operations** (60% of emissions)
   - CO₂: 540 kg/ton clinker (process)
   - Action: Alternative fuels, clinker substitution
   - Potential: -35% combined approach

2. **Raw Material Processing** (20% of emissions)
   - Action: Efficient grinding, VFD motors
   - Potential: -20% energy savings

3. **Fuel Combustion** (15% of emissions)
   - Action: RDF, biomass, waste oils
   - Potential: -45% with 60% alt fuel rate

4. **Transportation** (5% of emissions)
   - Action: Electric vehicles, route optimization
   - Potential: -30% with fleet upgrade

**Priority Actions:**
🎯 High impact: Kiln fuel switching, blast furnace biogas
🎯 Quick wins: Energy efficiency, waste heat recovery
🎯 Long-term: Process transformation, CCS`,
        suggestions: [
          'Show reduction roadmap',
          'Calculate department ROI',
          'Compliance requirements'
        ]
      };
    }

    // Compliance and regulations
    if (msg.includes('compliance') || msg.includes('regulation') || msg.includes('cop30') || msg.includes('standard')) {
      return {
        text: `📋 **Compliance & Regulatory Guidance**

**COP30 Requirements (2025):**
- Enhanced transparency framework
- Article 6 operationalization
- Sectoral decarbonization targets
- Just transition principles

**Verification Standards:**

**Gold Standard:**
- MRV frequency: Quarterly
- Third-party verification: Annual
- Sustainable development impacts required
- Premium: +20-30% credit value

**Verra VCS:**
- Project registration required
- Baseline methodology approval
- Monitoring plan submission
- Verification every 2 years

**ISO 14064:**
- GHG inventory standard
- Organizational & project level
- Required for corporate reporting

**Your Compliance Status:**
✅ MRV framework: Active
✅ Gold Standard: Verified
✅ Article 6: Eligible
⚠️ ISO 14064: Pending certification

**Upcoming Deadlines:**
- Q1 2025: MRV quarterly report
- March 2025: Gold Standard re-verification
- June 2025: Article 6 registry update

**Action Items:**
1. Complete ISO 14064 certification
2. Update baseline methodology
3. Submit Q4 monitoring report`,
        suggestions: [
          'MRV documentation checklist',
          'Verification cost estimates',
          'Regulatory updates'
        ]
      };
    }

    // Waste-to-energy optimization
    if (msg.includes('waste') || msg.includes('circular') || msg.includes('rdf') || msg.includes('biogas')) {
      return {
        text: `♻️ **Waste-to-Energy Optimization**

**Current Performance:**
- MSW processing: 450 tons/day
- RDF production: 180 tons/day (40% conversion)
- Biogas generation: 12,500 m³/day
- Coal displacement: 45%

**Optimization Opportunities:**

**1. Increase RDF Quality**
- Current calorific value: 3,800 kcal/kg
- Target: 4,500 kcal/kg (+18%)
- Actions: Better sorting, moisture control
- Impact: +15% cement kiln efficiency

**2. Biogas Yield Enhancement**
- Current: 85 m³/ton organic waste
- Target: 110 m³/ton (+29%)
- Actions: Co-digestion, temperature optimization
- Impact: 60% coal displacement in steel

**3. Expand Feedstock Sources**
- Industrial waste: +30% volume
- Agricultural residue: +25% volume
- Sewage sludge: +15% volume
- Impact: $1.8M additional revenue

**4. Energy Recovery Efficiency**
- Waste heat: 85% → 95% recovery
- Biogas upgrading: 60% → 75% CH₄
- Impact: +2,400 tCO₂e reductions/year

**Financial Impact:**
- Additional revenue: $3.2M/year
- Carbon credits: +4,500 ITMOs
- Payback period: 1.8 years

**Technology Recommendations:**
✓ AI-powered waste sorting (95% accuracy)
✓ Anaerobic digestion optimization
✓ Plasma gasification for hazardous waste`,
        suggestions: [
          'Calculate waste-to-energy ROI',
          'Technology vendor recommendations',
          'Feedstock supply chain'
        ]
      };
    }

    // Predictive analytics
    if (msg.includes('predict') || msg.includes('forecast') || msg.includes('scenario') || msg.includes('future')) {
      return {
        text: `🔮 **Predictive Analytics & Scenarios**

**2025-2030 Emission Forecast:**

**Business as Usual:**
- 2025: 45,200 tCO₂e
- 2027: 46,800 tCO₂e (+3.5%)
- 2030: 48,500 tCO₂e (+7.3%)
- Risk: Non-compliance with net-zero targets

**Moderate Action:**
- 2025: 42,100 tCO₂e (-7%)
- 2027: 36,500 tCO₂e (-19%)
- 2030: 31,200 tCO₂e (-31%)
- Investment: $8.5M | ROI: 3.2 years

**Aggressive Decarbonization:**
- 2025: 38,900 tCO₂e (-14%)
- 2027: 27,300 tCO₂e (-40%)
- 2030: 18,100 tCO₂e (-60%)
- Investment: $18.2M | ROI: 4.1 years

**AI-Predicted Risks:**
⚠️ Carbon price volatility: 65% probability
⚠️ Stricter regulations: 78% probability
⚠️ Supply chain disruption: 42% probability
✅ Technology cost reduction: 85% probability

**Recommended Path:**
1. 2025: Biogas + energy efficiency (-15%)
2. 2026-27: Alternative fuels + CCS pilot (-25%)
3. 2028-30: Process transformation (-60%)

**Financial Projection:**
- Carbon credit revenue: $12.4M (2025-2030)
- Avoided compliance costs: $8.7M
- Energy savings: $5.3M
- Total benefit: $26.4M`,
        suggestions: [
          'Customize scenario parameters',
          'Risk mitigation strategies',
          'Investment prioritization'
        ]
      };
    }

    // Supplier sustainability
    if (msg.includes('supplier') || msg.includes('supply chain') || msg.includes('vendor')) {
      return {
        text: `🔗 **Supplier Sustainability Scoring**

**Current Supplier Assessment:**

**High Performers (Score: 85-100):**
1. **Coal Supplier A** - Score: 92
   - Renewable energy: 45%
   - Emission intensity: -18% vs baseline
   - Certification: ISO 14001, SBTi

2. **Limestone Supplier B** - Score: 88
   - Rehabilitation program active
   - Electric fleet: 60%
   - Water recycling: 85%

**Medium Performers (Score: 60-84):**
3. **Transport Partner C** - Score: 76
   - Diesel fleet (improving)
   - Route optimization implemented
   - Action: EV transition plan needed

**Low Performers (Score: <60):**
4. **Chemical Supplier D** - Score: 54
   - No emission reporting
   - High carbon intensity
   - Action: Engagement or replacement

**Scoring Criteria:**
- Emission reporting (25%)
- Renewable energy use (20%)
- Circular practices (20%)
- Certifications (15%)
- Reduction targets (20%)

**Recommendations:**
✓ Engage low performers with improvement plans
✓ Incentivize high performers with long-term contracts
✓ Require Scope 3 reporting from all suppliers
✓ Collaborate on joint reduction initiatives

**Supply Chain Impact:**
- Scope 3 emissions: 28,400 tCO₂e (38% of total)
- Reduction potential: 8,500 tCO₂e (-30%)
- Supplier engagement ROI: 4.2x`,
        suggestions: [
          'Supplier engagement strategy',
          'Alternative supplier options',
          'Scope 3 reduction roadmap'
        ]
      };
    }

    // Default response with comprehensive menu
    return {
      text: `I can help you with:

🌍 **Emission Intelligence**
- Global trends for steel & cement industries
- Benchmark your plant performance
- Identify emission hotspots

💡 **Reduction Strategies**
- Technology recommendations
- Department-wise action plans
- ROI calculations

💰 **Carbon Markets**
- Trading opportunities
- Credit valuation
- Partnership matching

📊 **Analytics & Forecasting**
- Predictive scenarios
- Risk assessment
- Investment prioritization

✅ **Compliance Support**
- MRV documentation
- Verification standards
- Regulatory updates

♻️ **Circular Economy**
- Waste-to-energy optimization
- Material recovery
- Supply chain sustainability

What would you like to explore?`,
      suggestions: [
        'Show emission trends',
        'Reduction strategies for my plant',
        'Carbon credit opportunities',
        'Compliance requirements'
      ]
    };
  };

  const handleSend = async () => {
    if (!input.trim() && !selectedImage) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: input || 'Analyze this image',
      sender: 'user',
      timestamp: new Date(),
      image: selectedImage || undefined
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    const imageToAnalyze = selectedImage;
    setSelectedImage(null);
    setIsTyping(true);

    try {
      let responseText;
      
      if (imageToAnalyze) {
        responseText = await analyzeImage(imageToAnalyze);
      } else {
        const response = await getAIResponseFromBackend(input);
        responseText = response.text;
      }
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: responseText,
        sender: 'ai',
        timestamp: new Date(),
        suggestions: imageToAnalyze ? undefined : [
          'Tell me more about this',
          'What are the next steps?',
          'Show me alternatives'
        ]
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "I'm having trouble connecting to my reasoning engine. Please check if the backend server is running.",
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
  };

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <MessageSquare className="text-green-600" />
            AI Climate Assistant
          </h1>
          <p className="text-gray-600 mt-2">
            Intelligent support for emission reduction, carbon trading, and environmental compliance
          </p>
        </div>

        {/* Quick Action Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <button
            onClick={() => setInput('Show global emission trends')}
            className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <TrendingDown className="text-blue-600 mb-2" size={24} />
            <div className="text-sm font-medium text-gray-900">Emission Trends</div>
          </button>
          <button
            onClick={() => setInput('How to reduce emissions?')}
            className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
          >
            <Leaf className="text-green-600 mb-2" size={24} />
            <div className="text-sm font-medium text-gray-900">Reduce Emissions</div>
          </button>
          <button
            onClick={() => setInput('Carbon credit trading')}
            className="p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
          >
            <DollarSign className="text-yellow-600 mb-2" size={24} />
            <div className="text-sm font-medium text-gray-900">Carbon Credits</div>
          </button>
          <button
            onClick={() => setInput('Department pollution analysis')}
            className="p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            <AlertTriangle className="text-red-600 mb-2" size={24} />
            <div className="text-sm font-medium text-gray-900">Dept Analysis</div>
          </button>
          <button
            onClick={() => setInput('Waste-to-energy optimization')}
            className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
          >
            <Lightbulb className="text-purple-600 mb-2" size={24} />
            <div className="text-sm font-medium text-gray-900">Optimization</div>
          </button>
          <button
            onClick={() => setInput('Predict future scenarios')}
            className="p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
          >
            <BarChart3 className="text-indigo-600 mb-2" size={24} />
            <div className="text-sm font-medium text-gray-900">Forecasting</div>
          </button>
        </div>

        {/* Chat Interface */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200">
          {/* Messages */}
          <div className="h-[500px] overflow-y-auto p-6 space-y-4">
            {messages.map((message) => (
              <div key={message.id}>
                <div
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-4 ${
                      message.sender === 'user'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    {message.image && (
                      <img src={message.image} alt="Uploaded" className="max-w-xs rounded-lg mb-2" />
                    )}
                    <div className="whitespace-pre-wrap">{message.text}</div>
                    <div
                      className={`text-xs mt-2 ${
                        message.sender === 'user' ? 'text-green-100' : 'text-gray-500'
                      }`}
                    >
                      {message.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>

                {/* Suggestions */}
                {message.suggestions && message.sender === 'ai' && (
                  <div className="flex flex-wrap gap-2 mt-3 ml-2">
                    {message.suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-full hover:bg-gray-50 text-gray-700"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg p-4">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-4">
            {selectedImage && (
              <div className="mb-3 relative inline-block">
                <img src={selectedImage} alt="Upload preview" className="h-20 rounded-lg border" />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center gap-2"
                title="Upload image for analysis"
              >
                <Camera size={20} />
              </button>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask about emissions, carbon credits, compliance, or upload an image..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() && !selectedImage}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Send size={20} />
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AIAssistant;
